import { useEffect, useState, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { AppUpdate, AppUpdateAvailability } from "@capawesome/capacitor-app-update";
import { supabase } from "@/integrations/supabase/client";
import { logEvent } from "@/lib/mobile-diagnostics";

const CACHE_KEY = "app_update_check_v3";
const PENDING_KEY = "app_update_pending_attempt";
const DISMISSED_KEY = "app_update_dismissed_v2";
const CACHE_TTL_MS = 30 * 60 * 1000;

const ANDROID_PACKAGE = "com.atheniaai.decorcanvas";
const IOS_BUNDLE_ID = "com.atheniaai.decorcanvas";

const FALLBACK_URLS = {
  android: `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`,
  ios: "https://apps.apple.com/in/app/decor-canvas/id6758079242",
};

interface UpdateState {
  updateAvailable: boolean;
  storeVersion?: string;
  currentVersion?: string;
  lastError?: string;
}

// Compare semver-ish versions: returns true if current >= store
const isCurrentAtLeast = (current?: string, store?: string): boolean => {
  if (!current || !store) return false;
  const cParts = current.split(".").map((n) => parseInt(n, 10) || 0);
  const sParts = store.split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(cParts.length, sParts.length);
  for (let i = 0; i < len; i++) {
    const c = cParts[i] ?? 0;
    const s = sParts[i] ?? 0;
    if (c > s) return true;
    if (c < s) return false;
  }
  return true;
};

const clearCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {}
};

const isPluginUnavailable = (err: any): boolean => {
  const msg = (err?.message ?? String(err ?? "")).toLowerCase();
  const code = (err?.code ?? "").toString().toUpperCase();
  return (
    code === "UNIMPLEMENTED" ||
    msg.includes("not implemented") ||
    msg.includes("not available") ||
    msg.includes("unimplemented")
  );
};

// Server-side store lookup (no CORS, works for sideloaded builds too).
const fetchStoreVersionFromServer = async (
  platform: "ios" | "android",
): Promise<string | null> => {
  try {
    const { data, error } = await supabase.functions.invoke("check-app-version", {
      body: { platform },
    });
    if (error) {
      console.warn("[useAppUpdate] check-app-version error", error);
      return null;
    }
    return (data?.version as string | undefined) ?? null;
  } catch (err) {
    console.warn("[useAppUpdate] check-app-version failed", err);
    return null;
  }
};

export const useAppUpdate = () => {
  const [state, setState] = useState<UpdateState>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { ts, data } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL_MS) {
          // A cached positive can be stale after the user updates; revalidate it.
          if (data.updateAvailable && data.storeVersion && data.currentVersion) {
            return isCurrentAtLeast(data.currentVersion, data.storeVersion)
              ? { updateAvailable: false }
              : data;
          }
          return data;
        }
      }
    } catch {}
    return { updateAvailable: false };
  });

  const check = useCallback(async (): Promise<UpdateState> => {
    if (!Capacitor.isNativePlatform()) {
      return { updateAvailable: false };
    }
    const platform = Capacitor.getPlatform() as "ios" | "android";

    let current: string | undefined;
    let store: string | undefined;
    let pluginSaysAvailable = false;
    let lastError: string | undefined;

    // Always grab current version from CapApp (most reliable)
    try {
      const info = await CapApp.getInfo();
      current = info?.version;
    } catch {}

    // Try the native plugin
    try {
      const info: any = await AppUpdate.getAppUpdateInfo();
      pluginSaysAvailable =
        info?.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE;

      if (platform === "android") {
        // Android: plugin returns version CODES (numbers), not names.
        const availableCode = info?.availableVersionCode;
        const currentCode = info?.currentVersionCode;
        if (
          typeof availableCode === "number" &&
          typeof currentCode === "number" &&
          availableCode > currentCode
        ) {
          pluginSaysAvailable = true;
        }
        // Try to capture a name if the plugin happened to expose it
        if (info?.availableVersionName) store = info.availableVersionName;
        if (!current && info?.currentVersionName) current = info.currentVersionName;
      } else {
        if (info?.availableVersionName) store = info.availableVersionName;
        if (!current && info?.currentVersionName) current = info.currentVersionName;
      }

      console.log(
        `[useAppUpdate] platform=${platform} availability=${info?.updateAvailability} current=${current ?? "?"} store=${store ?? "?"} availCode=${info?.availableVersionCode ?? "?"} currCode=${info?.currentVersionCode ?? "?"}`,
      );
    } catch (err: any) {
      if (isPluginUnavailable(err)) {
        console.warn(
          "[useAppUpdate] native plugin not registered — run 'npx cap sync' and rebuild. Will use server fallback.",
        );
        lastError = "plugin not registered (run npx cap sync)";
      } else {
        console.warn("[useAppUpdate] plugin error, will use server fallback", {
          platform,
          name: err?.name,
          message: err?.message,
          code: err?.code,
        });
        lastError = err?.message ?? String(err);
      }
    }

    // Server-side store lookup — primary fallback for store *name*.
    // Always run when we don't yet have a store version string, so we can
    // (a) display it in the dialog and (b) cross-check against cached
    // plugin responses (iOS plugin caches ~24h).
    if (!store) {
      const remote = await fetchStoreVersionFromServer(platform);
      if (remote) store = remote;
    } else if (platform === "ios") {
      // Cross-check iOS plugin against live iTunes lookup to defeat the cache
      const remote = await fetchStoreVersionFromServer("ios");
      if (remote && !isCurrentAtLeast(store, remote)) {
        store = remote;
      }
    }

    // Final availability decision
    // Never trust the native plugin alone; a stale 24h iOS cache can keep
    // reporting UPDATE_AVAILABLE after the user already installed the update.
    let reallyAvailable = false;
    if (store && current) {
      reallyAvailable = !isCurrentAtLeast(current, store);
    } else if (store && !current) {
      // No current version but we got a store version — assume available
      reallyAvailable = true;
    }
    // If we don't have a store version, we cannot confirm an update exists.

    // Respect user's "don't ask again for this version"
    if (reallyAvailable && store) {
      try {
        const dismissed = localStorage.getItem(DISMISSED_KEY);
        if (dismissed === store) {
          reallyAvailable = false;
        } else if (dismissed && dismissed !== store) {
          localStorage.removeItem(DISMISSED_KEY);
        }
      } catch {}
    }

    const next: UpdateState = {
      updateAvailable: reallyAvailable,
      storeVersion: store,
      currentVersion: current,
      lastError,
    };

    setState(next);

    if (!reallyAvailable) {
      clearCache();
      try {
        localStorage.removeItem(PENDING_KEY);
      } catch {}
    } else {
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ ts: Date.now(), data: next }),
        );
      } catch {}
    }

    // Telemetry: log every check result (fire-and-forget)
    logEvent({
      category: "update",
      event_type: lastError ? "update_check_error" : "update_check",
      severity: lastError ? "error" : "info",
      message: lastError,
      context: {
        platform,
        current_version: current,
        store_version: store,
        update_available: reallyAvailable,
        plugin_says_available: pluginSaysAvailable,
      },
    });

    return next;
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    check();

    const sub = CapApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) check();
    });

    return () => {
      sub.then((s) => s.remove()).catch(() => {});
    };
  }, [check]);

  const openStore = useCallback(async () => {
    try {
      localStorage.setItem(PENDING_KEY, "1");
    } catch {}
    try {
      await AppUpdate.openAppStore();
    } catch (err) {
      console.log("[useAppUpdate] openAppStore failed, falling back:", err);
      const platform = Capacitor.getPlatform();
      const url =
        platform === "ios" ? FALLBACK_URLS.ios : FALLBACK_URLS.android;
      window.open(url, "_blank");
    }
  }, []);

  const dismissUpdate = useCallback((version?: string) => {
    if (!version) return;
    try {
      localStorage.setItem(DISMISSED_KEY, version);
      localStorage.removeItem(CACHE_KEY);
    } catch {}
    setState((prev) => ({ ...prev, updateAvailable: false }));
  }, []);

  const isDismissed = useCallback((version?: string): boolean => {
    if (!version) return false;
    try {
      return localStorage.getItem(DISMISSED_KEY) === version;
    } catch {
      return false;
    }
  }, []);

  return { ...state, openStore, dismissUpdate, isDismissed, recheck: check };
};
